import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";

const CURRENT_VERSION = "1.0";
const REQUIRED_DOCS = ["terms_of_use", "privacy_policy", "dpa"] as const;

export function useLegalAcceptance() {
  const { user, profile } = useAuth();
  const [needsAcceptance, setNeedsAcceptance] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) {
      setLoading(false);
      return;
    }

    // Quick check: if profile already has the current version, skip query
    const prof = profile as unknown as Record<string, unknown> | null;
    if (prof?.terms_version === CURRENT_VERSION && prof?.terms_accepted_at) {
      setNeedsAcceptance(false);
      setLoading(false);
      return;
    }

    const check = async () => {
      const { data, error } = await supabase
        .from("legal_acceptances")
        .select("document_type")
        .eq("user_id", user.id)
        .eq("document_version", CURRENT_VERSION);

      if (error) {
        console.error("Error checking legal acceptances:", error);
        setNeedsAcceptance(false); // Don't block on error
        setLoading(false);
        return;
      }

      const accepted = new Set(data?.map((r) => r.document_type));
      const allAccepted = REQUIRED_DOCS.every((d) => accepted.has(d));
      setNeedsAcceptance(!allAccepted);
      setLoading(false);
    };

    check();
  }, [user, profile]);

  const acceptAll = useCallback(async () => {
    if (!user) throw new Error("User not authenticated");

    const tenantId = user.user_metadata?.tenant_id || null;

    // Insert 3 acceptance records
    const records = REQUIRED_DOCS.map((docType) => ({
      user_id: user.id,
      tenant_id: tenantId,
      document_type: docType,
      document_version: CURRENT_VERSION,
      ip_address: null as string | null,
      user_agent: navigator.userAgent,
    }));

    const [upsertResult, profileResult] = await Promise.all([
      supabase
        .from("legal_acceptances")
        .upsert(records, { onConflict: "user_id,document_type,document_version" }),
      supabase
        .from("profiles")
        .update({
          terms_accepted_at: new Date().toISOString(),
          terms_version: CURRENT_VERSION,
        })
        .eq("user_id", user.id),
    ]);

    if (upsertResult.error) throw upsertResult.error;
    if (profileResult.error) console.error("Error updating profile terms:", profileResult.error);

    setNeedsAcceptance(false);
  }, [user]);

  return { needsAcceptance, loading, acceptAll };
}
